type ViteEnv = { env: Record<string, string | undefined> };

const ADMIN_API_BASE: string =
  (import.meta as unknown as ViteEnv).env.VITE_ADMIN_API_BASE ?? "";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${ADMIN_API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = (await res.json()) as { detail?: string };
      detail = body.detail ?? "";
    } catch {
      detail = await res.text();
    }
    throw new ApiError(res.status, detail || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type AuthConfigResponse = {
  enabled: boolean;
  google_client_id: string;
  allowed_email_domain: string;
};

export type AuthMeResponse = {
  user: {
    email: string;
    name: string;
    picture?: string | null;
  } | null;
  auth_required: boolean;
};

export type GoogleLoginResponse = {
  user: {
    email: string;
    name: string;
    picture?: string | null;
  };
};

export const api = {
  authConfig: () => request<AuthConfigResponse>("/auth/config"),
  me: () => request<AuthMeResponse>("/auth/me"),
  googleLogin: (code: string) =>
    request<GoogleLoginResponse>("/auth/google", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  logout: () => request<{ message: string }>("/auth/logout", { method: "POST" }),
  funnel: (params: FunnelParams) => {
    const query = new URLSearchParams({
      slug: params.slug,
      from: params.from,
      to: params.to,
      question_ids: params.questionIds.join(","),
      question_types: Object.entries(params.questionTypes)
        .map(([id, t]) => `${id}:${t}`)
        .join(","),
      // Top-line only. Per-question breakdowns are fetched lazily via
      // api.breakdown so no single request computes every question's breakdown
      // over the full window (that inline pass exceeded the 30s API timeout for
      // high-traffic quizzes). Steps still carry breakdown_kind; responses=[].
      include_breakdowns: "false",
    });
    if (params.cohort && params.cohort !== "all") query.set("cohort", params.cohort);
    if (params.persona) query.set("persona", params.persona);
    return request<FunnelResponse>(`/api/admin/analytics/funnel?${query.toString()}`);
  },
  breakdown: (params: BreakdownParams) => {
    const query = new URLSearchParams({
      slug: params.slug,
      from: params.from,
      to: params.to,
      question_id: params.questionId,
      question_type: params.questionType,
    });
    if (params.cohort && params.cohort !== "all") query.set("cohort", params.cohort);
    if (params.persona) query.set("persona", params.persona);
    return request<QuestionBreakdown>(
      `/api/admin/analytics/breakdown?${query.toString()}`,
    );
  },
  submissions: (params: {
    slug?: string;
    persona?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.slug) query.set("slug", params.slug);
    if (params.persona) query.set("persona", params.persona);
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("page_size", String(params.pageSize));
    const qs = query.toString();
    return request<SubmissionsResponse>(
      `/api/admin/submissions${qs ? `?${qs}` : ""}`,
    );
  },
  experimentStatus: (slug: string) =>
    request<ExperimentStatusResponse>(
      `/api/admin/experiments/status?slug=${encodeURIComponent(slug)}`,
    ),
};

export type ExperimentStatusResponse = {
  sessions_with_variant_last_hour: number;
};

export type BreakdownKind = "options" | "free_text" | "skip";

export type ResponseBreakdown = {
  answer_id: string;
  count: number;
  pct_of_answered: number;
  pct_of_started: number;
};

export type FunnelStep = {
  key: string;
  label: string;
  question_id: string | null;
  viewed: number;
  answered: number;
  drop_off_pct: number | null;
  exits: number;
  time_on_page_ms: number | null;
  breakdown_kind: BreakdownKind;
  denominator_answered: number;
  denominator_started: number;
  responses: ResponseBreakdown[];
};

export type DailyPoint = {
  date: string;
  visitors: number;
  submits: number;
  avg_duration_ms: number | null;
};

export type DeviceBreakdown = {
  desktop: number;
  mobile: number;
  tablet: number;
  unknown: number;
};

export type Facets = {
  applied: { cohort: string; persona: string | null };
  persona_counts: Record<string, number>;
};

export type FunnelResponse = {
  quiz_slug: string;
  started: number;
  completed: number;
  completion_rate: number | null;
  avg_duration_ms: number | null;
  steps: FunnelStep[];
  daily: DailyPoint[];
  devices: DeviceBreakdown;
  facets: Facets;
};

export type FunnelParams = {
  slug: string;
  from: string;
  to: string;
  questionIds: string[];
  questionTypes: Record<string, string>;
  cohort?: "all" | "completers" | "abandoners";
  persona?: string;
};

export type BreakdownParams = {
  slug: string;
  from: string;
  to: string;
  questionId: string;
  questionType: string;
  cohort?: "all" | "completers" | "abandoners";
  persona?: string;
};

export type QuestionBreakdown = {
  question_id: string;
  breakdown_kind: BreakdownKind;
  denominator_answered: number;
  denominator_started: number;
  responses: ResponseBreakdown[];
};

export type SubmissionRow = {
  id: number;
  quiz_slug: string;
  persona: string;
  email: string | null;
  phone: string | null;
  klaviyo_synced_at: string | null;
  klaviyo_error: string | null;
  created_at: string;
  intelligems_variant: string | null;
  session_id: string | null;
};

export type SubmissionsResponse = {
  total: number;
  items: SubmissionRow[];
  page: number;
  page_size: number;
};
